import React, { createContext, useContext, useEffect, useState } from 'react'
import { storage } from '@/lib/utils'

type Theme = 'light' | 'dark' | 'system'

interface ThemeContextType {
    theme: Theme
    setTheme: (theme: Theme) => void
    actualTheme: 'light' | 'dark' // The actual resolved theme
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export function useTheme() {
    const context = useContext(ThemeContext)
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider')
    }
    return context
}

interface ThemeProviderProps {
    children: React.ReactNode
    defaultTheme?: Theme
}

export function ThemeProvider({ children, defaultTheme = 'system' }: ThemeProviderProps) {
    const [theme, setTheme] = useState<Theme>(() => {
        return storage.get('theme', defaultTheme)
    })

    const [actualTheme, setActualTheme] = useState<'light' | 'dark'>('light')

    useEffect(() => {
        const root = window.document.documentElement

        const updateTheme = (newTheme: Theme) => {
            let resolvedTheme: 'light' | 'dark'

            if (newTheme === 'system') {
                resolvedTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
            } else {
                resolvedTheme = newTheme
            }

            root.classList.remove('light', 'dark')
            root.classList.add(resolvedTheme)
            setActualTheme(resolvedTheme)
        }

        // Update theme immediately
        updateTheme(theme)

        // Listen for system theme changes when using 'system'
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

        const handleSystemThemeChange = () => {
            if (theme === 'system') {
                updateTheme('system')
            }
        }

        mediaQuery.addEventListener('change', handleSystemThemeChange)

        return () => {
            mediaQuery.removeEventListener('change', handleSystemThemeChange)
        }
    }, [theme])

    const handleSetTheme = (newTheme: Theme) => {
        setTheme(newTheme)
        storage.set('theme', newTheme)
    }

    const value: ThemeContextType = {
        theme,
        setTheme: handleSetTheme,
        actualTheme,
    }

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    )
}